import { List } from "immutable";
import { type IInsightsDetectionResponseNode, InsightsDetectionModel } from "models/InsightsDetectionModel";
import {
	type IInsightsAccountResponseNode,
	type IInsightsIdentityResponseNode,
	InsightsIdentityModel
} from "models/InsightsIdentityModel";
import {
	type IDetectionImpactedEntityResponseNode,
	type IInsightsDetectionsGroupingResponseNode,
	TenantInsightsGroupingModel
} from "models/InsightsTenantGroupingModel";
import { graphqlGetAllInsightsPages, graphqlInsightsReq } from "utils/api/insightsGraphqlReq";
import { toGroupNativeMapBy } from "utils/toMapBy";
import {
	GET_IDENTITIES_DETECTIONS_QUERY,
	GET_DETECTION_IMPACTED_ENTITIES,
	GET_INSIGHTS_TENANT_QUERY,
	GET_IDENTITIES_QUERY,
	GET_ACCOUNTS_QUERY
} from "./queries/identityGraph.queries";

const GET_INSIGHTS_TENANT_VARIABLES = {
	lowSevFilter: {
		lte: 3
	},
	modSevFilter: {
		gt: 3,
		lte: 6
	},
	highSevFilter: {
		gt: 6,
		lte: 9
	},
	critSevFilter: {
		gt: 9
	}
};

export const getUsersDetections = async (emails: string[]) => {
	const variables = { emails };

	const data = await graphqlGetAllInsightsPages<IInsightsDetectionResponseNode[]>({
		query: GET_IDENTITIES_DETECTIONS_QUERY,
		variables
	});

	return List<InsightsDetectionModel>(data.map(node => InsightsDetectionModel.fromServerData(node)));
};

export const getInsightsIdentities = async (emails: string[]) => {
	const [identities, accounts] = await Promise.all([
		graphqlGetAllInsightsPages<IInsightsIdentityResponseNode[]>({
			query: GET_IDENTITIES_QUERY,
			variables: { emails },
			dataKey: "identities"
		}),
		graphqlGetAllInsightsPages<IInsightsAccountResponseNode[]>({
			query: GET_ACCOUNTS_QUERY,
			variables: { emails },
			dataKey: "accounts"
		})
	]);
	const accountsGroupedByEmail = toGroupNativeMapBy(accounts, ({ account }) => account.primaryEmail);
	const identitiesWithAccounts = identities.map(identity => {
		const {
			identity: { primaryEmail }
		} = identity;
		const associatedAccounts = identity.identity.associatedAccounts || accountsGroupedByEmail.get(primaryEmail);
		identity.identity.associatedAccounts = associatedAccounts;
		return identity;
	});

	return List<InsightsIdentityModel>(identitiesWithAccounts.map(node => InsightsIdentityModel.fromServerData(node)));
};

export const getTenantInsights = async () => {
	const { data } = await graphqlInsightsReq<IInsightsDetectionsGroupingResponseNode>({
		query: GET_INSIGHTS_TENANT_QUERY,
		variables: GET_INSIGHTS_TENANT_VARIABLES
	});

	const { lowDetections, moderateDetections, highDetections, criticalDetections } = data;

	const impactedEntitiesIds = [
		...lowDetections.nodes.flatMap(node => node.detectionDefinition.insights.flatMap(insight => insight.id)),
		...moderateDetections.nodes.flatMap(node => node.detectionDefinition.insights.flatMap(insight => insight.id)),
		...highDetections.nodes.flatMap(node => node.detectionDefinition.insights.flatMap(insight => insight.id)),
		...criticalDetections.nodes.flatMap(node => node.detectionDefinition.insights.flatMap(insight => insight.id))
	];

	const insightsImpactedEntitiesData = await graphqlGetAllInsightsPages<IDetectionImpactedEntityResponseNode[]>({
		query: GET_DETECTION_IMPACTED_ENTITIES,
		variables: {
			ids: impactedEntitiesIds
		},
		dataKey: "detectionImpactedEntities"
	});

	return TenantInsightsGroupingModel.fromServerData(data, insightsImpactedEntitiesData);
};
